import { ReactNode } from "react";
import { EditablePath } from "../editable/EditableContext";
import { EditableText } from "../editable/EditableText";

// Every editable text leaf in the report goes through this.
//
// admin-interface passes a `blockPath` down from its editor and wraps the tree
// in an EditableProvider, so each leaf becomes a click-to-edit field addressed
// by its path into the report payload. user-interface passes no path, and the
// leaf renders as a bare fragment — exactly the markup the client report had
// before any of this existed, with no extra wrapper element.
export const Ed = ({ path, value, render, multiline, placeholder, block }: {
  path?: EditablePath;
  value?: string;
  render?: (v: string) => ReactNode;
  multiline?: boolean;
  placeholder?: string;
  // The rendered value is block content (paragraphs): an inline wrapper around
  // it paints neither its dashed underline nor its hover, so the leaf looks
  // read-only.
  block?: boolean;
}) => {
  const text = value ?? "";
  if (!path) return <>{render ? render(text) : text}</>;
  // A freshly added element has empty fields, and an empty leaf with no
  // placeholder renders a zero-width span: visible to nobody, clickable by
  // nobody. The hint is what gives the editor something to aim at.
  //
  // The renderer is dropped for an empty value on purpose: some of them return
  // an empty array for "" (chunkProse does), and an empty array is truthy, so
  // EditableText would paint nothing and never fall through to the hint.
  return (
    <EditableText
      path={path}
      value={text}
      renderValue={text ? render : undefined}
      multiline={multiline}
      placeholder={placeholder ?? "Escribe aquí"}
      as={block ? "div" : undefined}
    />
  );
};

/** Extends a path with more segments, staying undefined when there is no path. */
export const at = (path: EditablePath | undefined, ...keys: Array<string | number>): EditablePath | undefined =>
  path ? [...path, ...keys] : undefined;
